import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

/**
 * **Out of the column and onto the page.** A scrim that says it covers the app
 * has to be a child of `<body>`, not of whatever the reader happened to be
 * looking at when they opened it.
 *
 * `position: fixed` measures against the viewport, so the scrim was always the
 * right *size*. What it is not free of is where it is *painted*: an ancestor
 * that opens a stacking context takes every descendant's `z-index` with it, and
 * the whole subtree is then stacked as one unit at the ancestor's own level. The
 * knowledge base's panel is `position: sticky` (`.knowledge-side` in knowledge.css) —
 * which opens a stacking context whatever its `z-index` says — so the graph's
 * detail modal inside it, `z-index: 5` and all, was ordered against the rest of
 * the page at the sticky column's `auto`. The nav rail is `z-index: 2` on the
 * root context, and 2 beats a subtree at 0: the scrim darkened the main column
 * and stopped dead at the rail, with the rail's links still clickable behind an
 * `aria-modal="true"` dialog.
 *
 * Raising the scrim's own `z-index` cannot fix that — no number inside a
 * stacking context escapes it. Moving the node does, and moving it to `body`
 * means no ancestor between the scrim and the root can ever do this again: not
 * this sticky column, not the transform or `contain` some later layout reaches
 * for. So the fix belongs here rather than in the one caller that tripped over
 * it.
 *
 * **The fallback is not a nicety.** Every spec in this package renders through
 * `renderToStaticMarkup` in vitest's node environment, where there is no
 * document to portal into and where `createPortal` throws. With no DOM there is
 * also no stacking to get wrong, so rendering the children where they stand is
 * both the only thing available and the right answer: the markup a static render
 * produces is the markup those specs have always asserted on.
 */
export function Portal({ children }: { children: ReactNode }) {
  if (typeof document === 'undefined') return <>{children}</>;
  return createPortal(children, document.body);
}
